import * as authService from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export interface EbayListing {
  title: string;
  price: number;
  condition: string;
  soldDate?: string;
  listedDate?: string;
  listingUrl: string;
  imageUrl?: string;
  sellerName?: string;
  sellerFeedbackScore?: number;
  sellerFeedbackPercentage?: string;
  shippingCost?: string;
  buyingOptions?: string[];
  itemLocation?: string;
  bidCount?: number;
}

export interface EbayPricingData {
  soldListings: EbayListing[];
  activeListings: EbayListing[];
  averagePrice: number;
  lowestPrice: number;
  highestPrice: number;
}

export interface EbayPricingResponse {
  cardName?: string;
  setName?: string;
  cardId?: string;
  ebay: EbayPricingData;
  lastUpdated: string;
}

export interface GradedPricingResponse {
  cardName: string;
  graded: {
    grade: string;
    query: string;
    soldListings: EbayListing[];
    activeListings: EbayListing[];
    averagePrice: number;
    lowestPrice: number;
    highestPrice: number;
  }[];
  lastUpdated: string;
}

export interface GradedPriceData {
  company: 'PSA' | 'BGS' | 'CGC' | 'SGC';
  grade: number;
  avgPrice: number;
  salesCount: number;
  listings: EbayListing[];
}

async function getAuthHeaders(): Promise<HeadersInit> {
  const token = await authService.getAccessToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export interface EbaySearchParams {
  cardName: string;
  cardNumber: string;
  setId: string;
}

/**
 * Search for eBay pricing using smart parameters
 */
export async function searchEbayPricing(
  params: EbaySearchParams,
): Promise<EbayPricingResponse> {
  try {
    const isOnePiece = /^(OP|EB|PRB|ST)\d*/i.test(params.setId);
    
    const requestBody = {
      cardName: params.cardName,
      cardNumber: params.cardNumber,
      setName: isOnePiece ? params.setId : '',
      game: isOnePiece ? 'onepiece' : 'pokemon'
    };

    console.log('[eBayPricingService] Searching with params:', requestBody);
    console.log(
      '[eBayPricingService] API URL:',
      `${API_BASE_URL}/api/public/pricing/search`,
    );

    const headers = await getAuthHeaders();
    console.log('[eBayPricingService] Headers:', headers);

    const response = await fetch(`${API_BASE_URL}/api/public/pricing/search`, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });

    console.log('[eBayPricingService] Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[eBayPricingService] Error response:', errorText);
      throw new Error(`Failed to fetch eBay pricing: ${response.statusText}`);
    }

    const data = await response.json();
    console.log('[eBayPricingService] Response data:', data);
    return data;
  } catch (error) {
    console.error('[eBayPricingService] Error fetching eBay pricing:', error);
    throw error;
  }
}

/**
 * Search for graded card pricing using the new backend API
 */
export async function searchGradedPricing(
  params: EbaySearchParams,
): Promise<GradedPricingResponse> {
  try {
    const isOnePiece = /^(OP|EB|PRB|ST)\d*/i.test(params.setId);
    
    const requestBody = {
      cardName: params.cardName,
      cardNumber: params.cardNumber,
      setName: isOnePiece ? params.setId : '',
      game: isOnePiece ? 'onepiece' : 'pokemon'
    };

    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/public/pricing/graded`, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch graded pricing: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('[eBayPricingService] Error fetching graded pricing:', error);
    throw error;
  }
}

/**
 * Get eBay pricing by cardId
 */
export async function getEbayPricing(
  cardId: string,
): Promise<EbayPricingResponse> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/public/pricing/${cardId}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch eBay pricing: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching eBay pricing:', error);
    throw error;
  }
}

/**
 * Parse grading information from eBay listing titles
 * Looks for patterns like "PSA 10", "BGS 9.5", "CGC 9", etc.
 */
function extractGradingInfo(
  title: string,
): { company: string; grade: number } | null {
  const gradingPatterns = [
    { company: 'PSA', pattern: /PSA\s+(\d+(?:\.\d+)?)/i },
    { company: 'BGS', pattern: /BGS\s+(\d+(?:\.\d+)?)/i },
    { company: 'CGC', pattern: /CGC\s+(\d+(?:\.\d+)?)/i },
    { company: 'SGC', pattern: /SGC\s+(\d+(?:\.\d+)?)/i },
  ];

  for (const { company, pattern } of gradingPatterns) {
    const match = title.match(pattern);
    if (match) {
      return {
        company,
        grade: parseFloat(match[1]),
      };
    }
  }

  return null;
}

/**
 * Group eBay listings by grading company and grade
 */
export function groupListingsByGrading(
  listings: EbayListing[],
): Record<string, Record<number, GradedPriceData>> {
  const grouped: Record<string, Record<number, GradedPriceData>> = {
    PSA: {},
    BGS: {},
    CGC: {},
    SGC: {},
  };

  listings.forEach((listing) => {
    const gradingInfo = extractGradingInfo(listing.title);
    if (gradingInfo) {
      const { company, grade } = gradingInfo;

      if (!grouped[company][grade]) {
        grouped[company][grade] = {
          company: company as 'PSA' | 'BGS' | 'CGC' | 'SGC',
          grade,
          avgPrice: 0,
          salesCount: 0,
          listings: [],
        };
      }

      grouped[company][grade].listings.push(listing);
      grouped[company][grade].salesCount++;
    }
  });

  // Calculate average prices
  Object.keys(grouped).forEach((company) => {
    Object.keys(grouped[company]).forEach((gradeKey) => {
      const grade = parseFloat(gradeKey);
      const data = grouped[company][grade];
      if (data.listings.length > 0) {
        const totalPrice = data.listings.reduce(
          (sum, listing) => sum + listing.price,
          0,
        );
        data.avgPrice = totalPrice / data.listings.length;
      }
    });
  });

  return grouped;
}

/**
 * Get graded card pricing summary
 */
export async function getGradedPricing(
  params: EbaySearchParams,
): Promise<Record<string, Record<number, GradedPriceData>>> {
  try {
    const pricingData = await searchEbayPricing(params);
    const allListings = [
      ...pricingData.ebay.soldListings,
      ...pricingData.ebay.activeListings,
    ];
    return groupListingsByGrading(allListings);
  } catch (error) {
    console.error('Error fetching graded pricing:', error);
    return { PSA: {}, BGS: {}, CGC: {}, SGC: {} };
  }
}
